import { supabase } from "@/lib/supabase-client";

export type CallDirection = "inbound" | "outbound";
export type CallStatus =
  | "initiated"
  | "ringing"
  | "in-progress"
  | "completed"
  | "busy"
  | "failed"
  | "no-answer"
  | "canceled";

export interface CallHistoryRecord {
  id: string;
  tenant_id: string;
  user_id?: string;
  from_number: string;
  to_number: string;
  direction: CallDirection;
  status: CallStatus;
  duration?: number;
  call_sid?: string;
  answered_at?: string;
  ended_at?: string;
  created_at: string;
  meta_data?: {
    caller_name?: string;
    error_message?: string;
    [key: string]: any;
  };
}

export const callService = {
  /**
   * Get call history for the current user/tenant
   */
  async getCallHistory(limit: number = 50, offset: number = 0): Promise<CallHistoryRecord[]> {
    const { data, error } = await supabase
      .from("call_history")
      .select("*")
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      throw new Error(`Failed to fetch call history: ${error.message}`);
    }

    return data as CallHistoryRecord[];
  },

  /**
   * Get recent calls (last 24 hours by default)
   */
  async getRecentCalls(hoursBack: number = 24, limit: number = 20): Promise<CallHistoryRecord[]> {
    const since = new Date(Date.now() - hoursBack * 60 * 60 * 1000).toISOString();

    const { data, error } = await supabase
      .from("call_history")
      .select("*")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      throw new Error(`Failed to fetch recent calls: ${error.message}`);
    }

    return data as CallHistoryRecord[];
  },

  /**
   * Get missed calls (incoming calls that were not answered)
   */
  async getMissedCalls(limit: number = 20): Promise<CallHistoryRecord[]> {
    const { data, error } = await supabase
      .from("call_history")
      .select("*")
      .eq("direction", "inbound")
      .in("status", ["no-answer", "busy", "failed"])
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      throw new Error(`Failed to fetch missed calls: ${error.message}`);
    }

    return data as CallHistoryRecord[];
  },

  /**
   * Get call history for a specific phone number
   */
  async getCallsForNumber(phoneNumber: string, limit: number = 10): Promise<CallHistoryRecord[]> {
    const { data, error } = await supabase
      .from("call_history")
      .select("*")
      .or(`from_number.eq.${phoneNumber},to_number.eq.${phoneNumber}`)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      throw new Error(`Failed to fetch calls for number: ${error.message}`);
    }

    return data as CallHistoryRecord[];
  },

  /**
   * Get call history between two specific phone numbers (both directions)
   */
  async getCallsBetween(number1: string, number2: string, limit: number = 50): Promise<CallHistoryRecord[]> {
    const { data, error } = await supabase
      .from("call_history")
      .select("*")
      .or(
        `and(from_number.eq.${number1},to_number.eq.${number2}),and(from_number.eq.${number2},to_number.eq.${number1})`,
      )
      .order("created_at", { ascending: true })
      .limit(limit);

    if (error) {
      throw new Error(`Failed to fetch calls between numbers: ${error.message}`);
    }

    return data as CallHistoryRecord[];
  },

  /**
   * Log a new call
   */
  async logCall(callData: {
    user_id?: string;
    from_number: string;
    to_number: string;
    direction: CallDirection;
    status?: CallStatus;
    call_sid?: string;
    meta_data?: Record<string, any>;
  }): Promise<CallHistoryRecord> {
    const { data, error } = await supabase
      .from("call_history")
      .insert({
        user_id: callData.user_id,
        from_number: callData.from_number,
        to_number: callData.to_number,
        direction: callData.direction,
        status: callData.status || "initiated",
        call_sid: callData.call_sid,
        meta_data: callData.meta_data || {},
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to log call: ${error.message}`);
    }

    return data as CallHistoryRecord;
  },

  /**
   * Update call status and metadata
   */
  async updateCall(
    callId: string,
    updates: {
      status?: CallStatus;
      duration?: number;
      answered_at?: string;
      ended_at?: string;
      meta_data?: Record<string, any>;
    },
  ): Promise<CallHistoryRecord> {
    const { data, error } = await supabase.from("call_history").update(updates).eq("id", callId).select().single();

    if (error) {
      throw new Error(`Failed to update call: ${error.message}`);
    }

    return data as CallHistoryRecord;
  },

  /**
   * Update call by Twilio call SID
   */
  async updateCallBySid(
    callSid: string,
    updates: {
      status?: CallStatus;
      duration?: number;
      answered_at?: string;
      ended_at?: string;
      meta_data?: Record<string, any>;
    },
  ): Promise<CallHistoryRecord | null> {
    const { data, error } = await supabase
      .from("call_history")
      .update(updates)
      .eq("call_sid", callSid)
      .select()
      .single();

    if (error) {
      // May not exist yet, that's ok
      console.warn(`Could not update call by SID ${callSid}:`, error.message);
      return null;
    }

    return data as CallHistoryRecord;
  },

  /**
   * Get call by Twilio call SID
   */
  async getCallBySid(callSid: string): Promise<CallHistoryRecord | null> {
    const { data, error } = await supabase.from("call_history").select("*").eq("call_sid", callSid).single();

    if (error) {
      return null;
    }

    return data as CallHistoryRecord;
  },

  /**
   * Delete old call history (cleanup)
   */
  async deleteOldCalls(daysOld: number = 90): Promise<number> {
    const cutoffDate = new Date(Date.now() - daysOld * 24 * 60 * 60 * 1000).toISOString();

    const { data, error } = await supabase.from("call_history").delete().lt("created_at", cutoffDate).select("id");

    if (error) {
      throw new Error(`Failed to delete old calls: ${error.message}`);
    }

    return data?.length || 0;
  },

  /**
   * Get call statistics
   */
  async getCallStats(daysBack: number = 7): Promise<{
    total: number;
    inbound: number;
    outbound: number;
    completed: number;
    missed: number;
    avgDuration: number;
  }> {
    const since = new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000).toISOString();

    const { data, error } = await supabase.from("call_history").select("*").gte("created_at", since);

    if (error) {
      throw new Error(`Failed to fetch call stats: ${error.message}`);
    }

    const calls = data as CallHistoryRecord[];

    const inbound = calls.filter((c) => c.direction === "inbound").length;
    const outbound = calls.filter((c) => c.direction === "outbound").length;
    const completed = calls.filter((c) => c.status === "completed").length;
    const missed = calls.filter(
      (c) => c.direction === "inbound" && ["no-answer", "busy", "failed"].includes(c.status),
    ).length;

    const completedCalls = calls.filter((c) => c.duration && c.duration > 0);
    const avgDuration =
      completedCalls.length > 0
        ? completedCalls.reduce((sum, c) => sum + (c.duration || 0), 0) / completedCalls.length
        : 0;

    return {
      total: calls.length,
      inbound,
      outbound,
      completed,
      missed,
      avgDuration: Math.round(avgDuration),
    };
  },
};
